import type { UIMessage } from "ai";

export type LiveTranscript = {
  role: "user" | "assistant";
  text: string;
  startMs: number;
  endMs: number;
  /** Chat insertion position when speech first arrives; retained as the turn grows. */
  anchorCount?: number;
  /** Confirmation speech stays visible but is not a new delegated request. */
  approvalOnly?: boolean;
};

export type LiveTranscriptBubble = LiveTranscript & { id: string };

/** Compatibility for the internal transcript envelopes saved by early Live sessions. */
export function cleanLiveRequestText(text: string): string {
  if (!text.startsWith("Help with the latest request in this live voice conversation. ")) return text;
  const boundary = text.indexOf("\n\n");
  if (boundary < 0 || !text.slice(0, boundary).includes("Existing tool approval requirements still apply.")) return text;
  const transcript = text.slice(boundary + 2);
  const turns = [...transcript.matchAll(/^(User|Live voice): ([\s\S]*?)(?=^(?:User|Live voice): |$(?![\s\S]))/gm)];
  return turns.filter((turn) => turn[1] === "User").at(-1)?.[2].trim() || "Voice request";
}

export function cleanLiveRequestMessage<T extends UIMessage>(message: T): T {
  if (message.role !== "user") return message;
  return { ...message, parts: message.parts.map((part) => part.type === "text"
    ? { ...part, text: cleanLiveRequestText(part.text) } : part) };
}

/** Pass safe agent results back even when the backend renders only a card. */
export function getLiveBackendResult(message?: UIMessage): string {
  if (message?.role !== "assistant") return "Check the chat for the backend result.";
  const results: string[] = [];
  for (const part of message.parts) {
    if (part.type === "text") { results.push(part.text); continue; }
    const tool = part as unknown as { type: string; state?: string; output?: Record<string, unknown> };
    if (tool.state !== "output-available" || !tool.output) continue;
    const output = tool.output;
    if (typeof output.error === "string") results.push(`Tool failed: ${output.error}`);
    else if (tool.type === "tool-list_agents" && Array.isArray(output.agents)) {
      const names = output.agents.flatMap((agent: unknown) =>
        agent && typeof agent === "object" && "name" in agent && typeof agent.name === "string" ? [agent.name] : []);
      results.push(names.length ? `Available agents: ${names.join(", ")}.` : "No available agents were found.");
    } else if (["tool-activate_agent", "tool-deactivate_current_agent"].includes(tool.type) && typeof output.message === "string") {
      results.push(output.message);
    }
  }
  return results.filter(Boolean).join("\n") || "Check the chat for the backend result or a question that needs your answer.";
}

/** Group each speaker independently: full-duplex acknowledgments must not
 * split a user sentence into several bubbles. Timing, not arrival order,
 * determines turns; a pause of 1.5 seconds starts a new bubble. */
export function groupLiveTranscript(fragments: LiveTranscript[]): LiveTranscriptBubble[] {
  const bubbles: LiveTranscriptBubble[] = [];
  for (const role of ["user", "assistant"] as const) {
    let current: LiveTranscriptBubble | undefined;
    for (const fragment of fragments.filter((part) => part.role === role && part.text.length > 0)
      .sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs)) {
      const previousEnd = current?.endMs;
      const otherSpeakerReplied = previousEnd !== undefined && fragments.some((part) =>
        part.role !== role && part.startMs >= previousEnd && part.endMs <= fragment.startMs);
      // An acknowledgment before backend work and narration after its answer
      // belong on opposite sides of that answer, even with no speech pause.
      const assistantPositionChanged = role === "assistant" && current &&
        fragment.anchorCount !== current.anchorCount && /[.!?…]["'”’)]*\s*$/.test(current.text);
      if (!current || assistantPositionChanged || otherSpeakerReplied || fragment.startMs - current.endMs >= 1_500) {
        current = { ...fragment, id: `${role}-${fragment.startMs}` };
        bubbles.push(current);
      } else {
        current.text += fragment.text;
        current.endMs = Math.max(current.endMs, fragment.endMs);
      }
    }
  }
  return bubbles.sort((a, b) => a.startMs - b.startMs);
}

// UTF-8 bytes are a conservative upper bound for token counts, including
// non-English speech. Live allows 500 tokens per append and 8,192 in history.
export function splitLiveContent(text: string, maxBytes = 400): string[] {
  const encoder = new TextEncoder();
  const chunks: string[] = [];
  let chunk = "";
  let bytes = 0;
  for (const character of text) {
    const length = encoder.encode(character).length;
    if (bytes + length > maxBytes && chunk) {
      // Prefer a word boundary so incremental speech updates stay readable.
      const boundary = Math.max(chunk.lastIndexOf(" "), chunk.lastIndexOf("\n")) + 1;
      const end = boundary > chunk.length / 2 ? boundary : chunk.length;
      chunks.push(chunk.slice(0, end));
      chunk = chunk.slice(end);
      bytes = encoder.encode(chunk).length;
    }
    chunk += character;
    bytes += length;
  }
  if (chunk) chunks.push(chunk);
  return chunks;
}

export function getLiveHistory(messages: UIMessage[]) {
  let remaining = 6_000;
  const history: { role: "user" | "assistant"; text: string }[] = [];
  for (const message of messages.slice(-32).reverse()) {
    if (message.role !== "user" && message.role !== "assistant") continue;
    const text = message.parts.filter((part) => part.type === "text")
      .map((part) => part.text).join("\n");
    if (!text.trim()) continue;
    const clipped = splitLiveContent(text, remaining)[0];
    history.unshift({ role: message.role, text: clipped });
    remaining -= new TextEncoder().encode(clipped).length;
    if (remaining < 100) break;
  }
  return history;
}

export function formatLiveTranscript(fragments: LiveTranscript[], offsetMs: number): string {
  const turns: { role: string; text: string }[] = [];
  for (const fragment of [...fragments].filter((part) => part.startMs <= offsetMs)
    .sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs)) {
    const last = turns.at(-1);
    if (last?.role === fragment.role) last.text += fragment.text;
    else turns.push({ role: fragment.role, text: fragment.text });
  }
  return turns.map((turn) => `${turn.role === "user" ? "User" : "Live voice"}: ${turn.text}`).join("\n");
}

/** Only carry new voice context; earlier delegated work is already in chat history. */
export function getLiveDelegationRequest(fragments: LiveTranscript[], offsetMs: number, previousOffsetMs = -Infinity): string | null {
  const turns = groupLiveTranscript(fragments.filter((part) => !part.approvalOnly && part.startMs <= offsetMs && part.startMs > previousOffsetMs));
  const latestUserIndex = turns.findLastIndex((turn) => turn.role === "user");
  if (latestUserIndex < 0) return null;
  const latest = turns[latestUserIndex];
  // Bound older, standalone voice context without clipping the current request.
  const context: string[] = [];
  let bytes = 0;
  for (const turn of turns.slice(0, latestUserIndex).reverse()) {
    const line = `${turn.role === "user" ? "User" : "Live voice"}: ${turn.text}`;
    const length = new TextEncoder().encode(line).length;
    if (bytes + length > 2_000 || context.length === 4) break;
    context.unshift(line);
    bytes += length;
  }
  return [...context, `User: ${latest.text}`].join("\n");
}

/** Small acknowledgments don't replace a task. Unknown/new speech does. */
export function hasNewLiveRequest(fragments: LiveTranscript[], offsetMs: number): boolean {
  return groupLiveTranscript(fragments.filter((part) => !part.approvalOnly && part.startMs > offsetMs)).some((turn) => {
    if (turn.role !== "user") return false;
    const text = turn.text.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, " ").replace(/\s+/g, " ").trim();
    return Boolean(text) && !/^(?:(?:ok|okay|right|sure|thanks|thank you|got it|understood|uh huh|mm hmm|mhm)(?:\s+|$))+$/.test(text);
  });
}

/** Refresh Live's routing context whenever chat selection changes. */
export function getLiveAgentContext(agentId: string | null, agentName: string | null, model: string): string {
  return JSON.stringify({
    selectedAgent: agentId ? { id: agentId, name: agentName || "Selected agent" } : null,
    backendModel: model,
    routing: "Use the selected agent for delegations requiring its expertise, private context, tools, actions or explicitly requested perspective, and follow-ups to its ongoing task. Handle casual conversation and simple standalone requests directly. Selection changes the delegation destination, not your voice identity. Credit agent-authored answers; never attribute your own answers to the agent. Present delegated results conversationally without changing their meaning.",
  });
}
